import React, { useCallback, useEffect, useMemo, useState } from "react";

import { IconButton } from "@mui/material";
import { useParams } from "react-router-dom";

import { Entry } from "@fartherfinance/frontend/api/Investment/types";
import useGetTransitionProposal from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetTransitionProposal";
import useGetTransitionProposalPerformanceVisibilityVisibility from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetTransitionProposalPerformanceVisibility";
import usePatchTransitionProposal from "@fartherfinance/frontend/api/TransitionProposals/hooks/usePatchTransitionProposal";
import usePatchTransitionProposalPerformanceVisibility from "@fartherfinance/frontend/api/TransitionProposals/hooks/usePatchTransitionProposalPerformanceVisibility";
import {
  TransitionProposalId,
  TransitionProposalPerformanceMetric,
  TransitionProposalPortfolioView,
} from "@fartherfinance/frontend/api/TransitionProposals/types";
import { PortfolioPlanId } from "@fartherfinance/frontend/api/Types";

import { TransitionProposalFlowStateData } from "../../types";
import Caret from "@src/assets/svg/components/Caret";
import TransitionProposalDetails from "@src/multiCustodian/components/TransitionProposals/TransitionProposalDetails/TransitionProposalDetails";
import {
  Section,
  SectionBody,
  SectionHeader,
} from "@src/multiCustodian/components/TransitionProposals/TransitionProposalDetails/TransitionProposalDetailsSections";
import { useGetModelPortfolioByName } from "@src/multiCustodian/hooks/useGetModelPortfolioByName";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import Conditional from "@src/sharedComponents/Conditional/Conditional";
import { actions } from "@src/sharedComponents/FlowContainer/reducer/actions";
import { useFlowContainerContext } from "@src/sharedComponents/FlowContainer/reducer/Context";
import GridRow from "@src/sharedComponents/GridRow/GridRow";
import VerticalDivider from "@src/sharedComponents/VerticalDivider/VerticalDivider";
import { handleApiError } from "@src/utils/handleApiError";

import {
  defaultModelPortFolioList,
  ModelPortfolioTypeOptions,
} from "./constants";
import { getTransformArgs, transformPortfolioResponseData } from "./helpers";
import PortfolioDataList from "./PortfolioDataList/PortfolioDataList";
import {
  DefaultListItemOption,
  ListItemOption,
  ModelPortfolioQueryParams,
} from "./types";

import styles from "./Share.module.css";

const PAGE_SIZE = 100;

const {
  CUSTOM_PORTFOLIOS,
  FARTHER_LIST,
  SHARED_BY_ADVISOR,
  FARTHER_FAMILY_BY_ID,
  PORTFOLIO_PLAN_BY_ID,
} = ModelPortfolioTypeOptions;

const Share = () => {
  const { transitionProposalId } = useParams<{
    transitionProposalId: TransitionProposalId;
  }>();
  const [queryParams, setQueryParams] = useState<ModelPortfolioQueryParams>({
    name: FARTHER_LIST,
    id: null,
  });
  const [listData, setListData] = useState<ListItemOption[]>([]);
  const [defaultListData, setDefaultListData] = useState<
    DefaultListItemOption[]
  >(defaultModelPortFolioList);
  const [listItemStack, setListItemStack] = useState<ListItemOption[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [selectedModelPortfolio, setSelectedModelPortfolio] = useState<
    ListItemOption | undefined
  >(undefined);
  const [_securityEntries, setSecurityEntries] = useState<Entry[]>([]);
  const [hasCurrentProposalError, setHasCurrentProposalError] = useState(false);
  const [hasProposedProposalError, setHasProposedProposalError] =
    useState(false);

  const { dispatch } =
    useFlowContainerContext<Partial<TransitionProposalFlowStateData>>();

  const auth = useRequestAuth();

  const notify = useStatusNotification();

  const {
    data: transitionProposalResponse,
    isLoading: isLoadingTransitionProposal,
  } = useGetTransitionProposal(transitionProposalId, auth);

  const { data: metricVisibilitySettings } =
    useGetTransitionProposalPerformanceVisibilityVisibility(
      transitionProposalId,
      auth
    );

  const { mutateAsync: patchTransitionProposalMetricVisibility } =
    usePatchTransitionProposalPerformanceVisibility(auth);

  const metricVisibilitySettingsForCurrentPortfolio = useMemo(
    () => metricVisibilitySettings?.data.current || {},
    [metricVisibilitySettings?.data]
  );

  const metricVisibilitySettingsForProposedPortfolio = useMemo(
    () => metricVisibilitySettings?.data.proposed || {},
    [metricVisibilitySettings?.data]
  );

  const {
    mutateAsync: patchTransitionProposal,
    isLoading: isMutatingProposal,
  } = usePatchTransitionProposal(auth);

  useEffect(() => {
    const transitionProposal = transitionProposalResponse?.data;

    if (
      !transitionProposal ||
      isLoadingTransitionProposal ||
      isMutatingProposal
    ) {
      return;
    }
    if (
      transitionProposal?.proposedPortfolioPlanId !== null &&
      queryParams.id !== transitionProposal.proposedPortfolioPlanId &&
      queryParams.name !== PORTFOLIO_PLAN_BY_ID
    ) {
      setQueryParams({
        name: PORTFOLIO_PLAN_BY_ID,
        id: transitionProposal.proposedPortfolioPlanId as PortfolioPlanId,
      });
    }
    // Only set the proposed portfolio plan id if it is not already set on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const queryResponse = useGetModelPortfolioByName({
    queryParams,
    page: page.toString(),
    pageSize: PAGE_SIZE.toString(),
    search: searchTerm,
  });

  const {
    data: queryResponseData,
    isLoading: isLoadingQuery,
    pagination,
  } = useMemo(() => {
    const noPaginationFallback = {
      currentPage: 1,
      pageCount: 0,
      totalCount: 0,
      pageSize: 1,
    };

    if (queryParams?.name === undefined) {
      return {
        data: [],
        isLoading: false,
        hasError: false,
        pagination: noPaginationFallback,
      };
    }

    const queryName = queryParams.name;

    return {
      ...queryResponse[queryName],
      data: queryResponse[queryName]?.data?.data,
      pagination:
        queryName === SHARED_BY_ADVISOR || queryName === FARTHER_LIST
          ? queryResponse[queryName]?.data?.pagination
          : noPaginationFallback,
    };
  }, [queryResponse, queryParams]);

  const hasMore = useMemo(() => {
    return (
      pagination !== undefined &&
      listData.length < pagination?.totalCount &&
      listData.length >= PAGE_SIZE
    );
  }, [listData, pagination]);

  const getTransformedResponseData = useCallback(
    (type: ModelPortfolioTypeOptions) => {
      const result = getTransformArgs(type, queryResponseData);

      if (!result) {
        return undefined;
      }

      const transformed = transformPortfolioResponseData(result);

      return transformed;
    },
    [queryResponseData]
  );

  const setProposedPortfolioPlanId = useCallback(
    async (proposedPortfolioPlan: ListItemOption | undefined) => {
      if (proposedPortfolioPlan === undefined) {
        throw new Error("Proposed portfolio plan is undefined");
      }
      const body = {
        proposedPortfolioPlanId: proposedPortfolioPlan.id as PortfolioPlanId,
      };
      try {
        await patchTransitionProposal({
          transitionProposalId,
          body,
        });
      } catch (error) {
        handleApiError({
          error,
          errorMessage: "Failed to set proposed portfolio plan",
          genericErrorMessage: "Something went wrong while updating the plan",
          notify,
        });
      }
    },
    [notify, patchTransitionProposal, transitionProposalId]
  );

  useEffect(() => {
    if (!queryResponseData || isLoadingQuery || !queryParams?.name) {
      return;
    }

    (async () => {
      const transformedData = getTransformedResponseData(queryParams.name);

      if (transformedData === undefined) {
        return;
      }

      const name = queryParams.name;

      if (name === PORTFOLIO_PLAN_BY_ID) {
        const selectedItem = transformedData.find(
          (item) => item.id === queryParams.id
        );

        if (selectedItem) {
          setSelectedModelPortfolio(selectedItem);
          setSecurityEntries(selectedItem.securityEntries ?? []);
        }
        return;
      }

      setListData((prev) =>
        page > 1 ? [...prev, ...transformedData] : transformedData
      );
    })();
  }, [
    queryResponseData,
    isLoadingQuery,
    queryParams,
    page,
    getTransformedResponseData,
    setProposedPortfolioPlanId,
  ]);

  const handleOnClickDefaultItem = (item: DefaultListItemOption) => () => {
    if (item.type === CUSTOM_PORTFOLIOS) {
      dispatch({
        type: actions.SET_DATA,
        payload: { showCustomPortfolioModal: true },
      });
    } else {
      setListItemStack((prev) => [...prev, item]);

      if (item.type === FARTHER_FAMILY_BY_ID) {
        const result = getTransformedResponseData(FARTHER_LIST);

        if (result === undefined) {
          return;
        }

        const [_FAM, ...fartherModelMarketPlaceData] = result;

        setListData([...fartherModelMarketPlaceData]);

        setQueryParams({
          name: FARTHER_FAMILY_BY_ID,
          id: null,
        });
      } else if (item.type === FARTHER_LIST) {
        const result = getTransformedResponseData(FARTHER_LIST);

        if (result === undefined) {
          return;
        }

        const [FAM] = result;

        setQueryParams({
          name: FAM.type,
          id: FAM.id,
        });
      } else {
        setQueryParams({
          name: item.type,
          id: item.id,
        });
      }
      setDefaultListData([]);
    }
  };

  const handleOnClickChildItem = useCallback(
    (item: ListItemOption) => async () => {
      setListItemStack((prev) => [...prev, item]);

      const shouldSetProposedPortfolio = [
        PORTFOLIO_PLAN_BY_ID,
        SHARED_BY_ADVISOR,
      ].includes(item.type);

      if (shouldSetProposedPortfolio) {
        await setProposedPortfolioPlanId(item);
      }

      const shouldSetSelectedModelPortfolio = [
        FARTHER_LIST,
        SHARED_BY_ADVISOR,
      ].includes(item.type);

      if (shouldSetSelectedModelPortfolio) {
        setSelectedModelPortfolio(item);
      } else {
        setQueryParams({
          name: item.type,
          id: item.id,
        });
      }

      setPage(1);
    },
    [
      setListItemStack,
      setSelectedModelPortfolio,
      setProposedPortfolioPlanId,
      setQueryParams,
      setPage,
    ]
  );

  const handleOnEndReached = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0].isIntersecting;

      if (isIntersecting && !isLoadingQuery && hasMore) {
        setPage((prev) => prev + 1);
      }
    },
    [isLoadingQuery, hasMore]
  );

  const handleOnSearch = (term: string) => {
    setPage(1);
    setSearchTerm(term);
  };

  const handleOnResetList = useCallback(() => {
    setQueryParams({
      name: FARTHER_LIST,
      id: null,
    });
    setListItemStack([]);
    setSelectedModelPortfolio(undefined);
    setDefaultListData(defaultModelPortFolioList);
    setListData([]);
    setSecurityEntries([]);
    setSearchTerm("");
    setPage(1);
  }, []);

  const handleOnClearSearch = () => {
    setSearchTerm("");
  };

  const handleBackButtonClick = useCallback(() => {
    if (
      transitionProposalResponse?.data?.proposedPortfolioPlanId !== null &&
      listItemStack.length === 0
    ) {
      handleOnResetList();
      return;
    }

    if (selectedModelPortfolio !== undefined || _securityEntries.length > 0) {
      setSelectedModelPortfolio(undefined);
      setSecurityEntries([]);
    }

    setListItemStack((prevStack) => {
      if (prevStack.length === 0) {
        return prevStack;
      }

      const newStack = [...prevStack];

      newStack.pop();

      const previousItem = newStack[newStack.length - 1];

      if (!previousItem) {
        setQueryParams({
          name: FARTHER_LIST,
          id: null,
        });
        setDefaultListData(defaultModelPortFolioList);
        setListData([]);
      } else {
        if (previousItem.type === FARTHER_LIST) {
          const result = getTransformedResponseData(FARTHER_FAMILY_BY_ID);

          if (result !== undefined) {
            const [FAM] = result;

            setQueryParams({
              name: FAM.type,
              id: FAM.id,
            });
          }
        } else {
          setQueryParams({
            name: previousItem.type,
            id: previousItem.id,
          });
        }
      }

      setPage(1);
      setSearchTerm("");

      return newStack;
    });
  }, [
    selectedModelPortfolio,
    _securityEntries,
    getTransformedResponseData,
    handleOnResetList,
    listItemStack,
    transitionProposalResponse,
  ]);

  const currentStackListItem = useMemo(() => {
    if (listItemStack.length === 0) {
      return [];
    }

    return listItemStack[listItemStack.length - 1];
  }, [listItemStack]);

  const onToggleMetricVisibility = useCallback(
    (view: TransitionProposalPortfolioView) =>
      (key: TransitionProposalPerformanceMetric) =>
      async () => {
        const visible = !metricVisibilitySettings?.data[view][key];
        const body = {
          portfolioView: view,
          metrics: [key],
          visible,
        };
        try {
          await patchTransitionProposalMetricVisibility({
            transitionProposalId: transitionProposalId,
            body,
          });
        } catch (error) {
          handleApiError({
            error,
            errorMessage: "Failed to set metric visibility",
            genericErrorMessage:
              "Something went wrong while setting the visibility",
            notify,
          });
        }
      },
    [
      metricVisibilitySettings,
      patchTransitionProposalMetricVisibility,
      transitionProposalId,
      notify,
    ]
  );

  const handleOnClickBackButton = useCallback(async () => {
    const shouldGoBack =
      selectedModelPortfolio !== undefined ||
      _securityEntries.length > 0 ||
      listItemStack.length > 0;

    if (transitionProposalResponse?.data?.proposedPortfolioPlanId !== null) {
      try {
        await patchTransitionProposal({
          transitionProposalId,
          body: {
            proposedPortfolioPlanId: null,
          },
        });

        if (shouldGoBack) {
          handleBackButtonClick();
        } else {
          handleOnResetList();
        }
      } catch (error) {
        handleApiError({
          error,
          errorMessage: "Failed to clear proposed portfolio plan",
          genericErrorMessage: "Something went wrong while updating the plan",
          notify,
        });
      }
      return;
    }

    if (shouldGoBack) {
      handleBackButtonClick();
    } else {
      handleOnResetList();
    }
  }, [
    selectedModelPortfolio,
    _securityEntries,
    listItemStack,
    handleBackButtonClick,
    handleOnResetList,
    transitionProposalResponse,
    notify,
    patchTransitionProposal,
    transitionProposalId,
  ]);

  const isBackButtonDisabled = useMemo(() => {
    return (
      queryParams?.name === undefined &&
      !selectedModelPortfolio &&
      defaultListData.length > 0
    );
  }, [queryParams?.name, selectedModelPortfolio, defaultListData.length]);

  const selectedModelPortfolioTitle = useMemo(() => {
    if (listItemStack.length > 0) {
      return listItemStack[listItemStack.length - 1].title;
    }

    return selectedModelPortfolio !== undefined
      ? selectedModelPortfolio.title
      : "Select Portfolio";
  }, [selectedModelPortfolio, listItemStack]);

  const isTransitionProposalShared = useMemo(
    () => transitionProposalResponse?.data?.status === "shared",
    [transitionProposalResponse?.data?.status]
  );

  // Expose any errors from TransitionProposalDetails to the parent component through context
  useEffect(() => {
    dispatch({
      type: actions.SET_DATA,
      payload: {
        hasTransitionProposalDetailsError:
          hasCurrentProposalError || hasProposedProposalError,
      },
    });
  }, [hasCurrentProposalError, hasProposedProposalError, dispatch]);

  const handleCurrentProposalError = (hasError: boolean) => {
    setHasCurrentProposalError(hasError);
  };

  const handleProposedProposalError = (hasError: boolean) => {
    setHasProposedProposalError(hasError);
  };

  return (
    <GridRow className={styles.gridRow}>
      <Section>
        <SectionHeader title="Current Portfolio" />
        <SectionBody>
          <TransitionProposalDetails
            transitionProposalId={transitionProposalId}
            metricVisibilitySettings={
              metricVisibilitySettingsForCurrentPortfolio
            }
            onToggleMetricVisibility={onToggleMetricVisibility("current")}
            disableMetricVisibilityToggle={isTransitionProposalShared}
            onError={handleCurrentProposalError}
          />
        </SectionBody>
      </Section>

      <VerticalDivider label="vs." />

      <Section>
        <SectionHeader
          title={selectedModelPortfolioTitle}
          startComponent={
            <Conditional condition={!isTransitionProposalShared}>
              <IconButton
                disabled={isBackButtonDisabled}
                className={styles.backButton}
                onClick={handleOnClickBackButton}
              >
                <Caret rotate="left" />
              </IconButton>
            </Conditional>
          }
        />
        <SectionBody>
          <PortfolioDataList
            queryParams={queryParams}
            selectedModelPortfolio={selectedModelPortfolio}
            listData={listData}
            defaultListData={defaultListData}
            searchTerm={searchTerm}
            isLoadingQuery={isLoadingQuery}
            currentStackListItem={currentStackListItem}
            handleOnClickDefaultItem={handleOnClickDefaultItem}
            handleOnClickChildItem={handleOnClickChildItem}
            handleOnEndReached={handleOnEndReached}
            handleOnSearch={handleOnSearch}
            handleOnClearSearch={handleOnClearSearch}
            handleBackButtonClick={handleBackButtonClick}
            height={700}
            metricVisibilitySettings={
              metricVisibilitySettingsForProposedPortfolio
            }
            onToggleMetricVisibility={onToggleMetricVisibility("proposed")}
            showSearchClearButton={
              queryParams?.name !== undefined && searchTerm !== ""
            }
            disableMetricVisibilityToggle={isTransitionProposalShared}
            onError={handleProposedProposalError}
          />
        </SectionBody>
      </Section>
    </GridRow>
  );
};

export default Share;
